import {
  call, takeLatest, put, select,
  delay,
} from 'redux-saga/effects';
import { api } from '@/utils/api';
import onboardingActions from './onboarding.actions';
import { OnboardingActionTypes } from './onboarding.types';
import logger from '@/utils/logger';
import { ONBOARDING_REGISTER_URL, WIZARD_REGISTER_URL } from '@/config/api';
import { COUNTRY } from '@/config';
import { ATIVACAO_UPDATE_ONBOARDING_FLOW_EVERY_STEP_CHANGE } from '@/config/GrowthBook/constants';
import { isBRServer } from '@/utils/Validators/validation';
import { commonActions } from '../common';
import { locale } from '@/utils/locale';

function* registerOnboarding(action) {
  const { token } = yield select(state => state.auth);
  const {
    hostingId, domain, origin, skip,
  } = action.payload;

  const request = {
    url: ONBOARDING_REGISTER_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      'x-brand': COUNTRY,
    },
    data: { domain, origin, skip },
  };


  try {
    const response = yield call(api.whmcs.post, request);
    const builderId = response.data.data.id;
    yield put(onboardingActions.onboarding.register.success({ builderId }));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas onboarding - method registerOnboarding', error);
    yield put(onboardingActions.onboarding.register.failure());
  }
}

function* updateOnboarding(action) {
  const { token } = yield select(state => state.auth);
  const {
    hostingId, domain, origin, builder, finished, step,
  } = action.payload;

  const request = {
    url: ONBOARDING_REGISTER_URL(hostingId),
    headers: {
      Authorization: `Bearer ${token}`,
      'x-brand': COUNTRY,
    },
    data: {
      domain, origin, builder, finished, step,
    },
  };

  try {
    yield call(api.whmcs.patch, request);
    yield put(onboardingActions.onboarding.update.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas onboarding - method registerOnboarding', error);
    yield put(onboardingActions.onboarding.update.failure());
  }
}

function* registerWizard(action) {
  const { token } = yield select(state => state.auth);
  const { domain, origin } = action.payload;

  const request = {
    url: WIZARD_REGISTER_URL(),
    headers: {
      Authorization: `Bearer ${token}`,
      'x-brand': COUNTRY,
    },
    data: { domain, origin },
  };

  try {
    const response = yield call(api.whmcs.post, request);
    yield put(onboardingActions.wizard.register.set({ wizardId: response.data.data.id }));
    yield put(onboardingActions.wizard.register.success());
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas onboarding - method registerOnboarding', error);
    yield put(onboardingActions.wizard.register.failure());
  }
}

function* updateWizard(action) {
  const { token } = yield select(state => state.auth);
  const { wizard } = yield select(state => state.newOnboarding);
  const commonReducer = yield select(state => state.common);
  const {
    domain, origin, builder, finished,
  } = action.payload;
  const preventChangeStart = commonReducer && commonReducer.toggles[ATIVACAO_UPDATE_ONBOARDING_FLOW_EVERY_STEP_CHANGE];

  const request = {
    url: WIZARD_REGISTER_URL(wizard.id),
    headers: {
      Authorization: `Bearer ${token}`,
      'x-brand': COUNTRY,
    },
    data: {
      domain, origin, builder, finished,
    },
  };

  try {
    yield call(api.whmcs.patch, request);
    yield put(onboardingActions.wizard.update.success(preventChangeStart));
  } catch (requestError) {
    const error = { ...requestError, userRequestData: request };
    logger.error('Error sagas onboarding - method registerOnboarding', error);
    yield put(onboardingActions.wizard.update.failure());
  }
}

function* requestBusinessListBuilder(action) {
  const { query } = action.payload;
  const { token } = yield select(state => state.auth);
  const { jwt: jwtToken } = yield select(state => state.auth);

  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    data: {
      input: query,
    },
    url: 'api/v3/builder/business',
  };

  try {
    yield delay(400);

    if (!query || query.trim().length === 0) {
      yield put(onboardingActions.sitebuilder.business.set([]));
      return;
    }

    const response = yield call(api.whmcs.post, request);
    const { business } = response.data;

    yield put(onboardingActions.sitebuilder.business.set(business));
    yield put(onboardingActions.sitebuilder.business.success());
  } catch (e) {
    console.log('erro', e);
    yield put(onboardingActions.sitebuilder.business.failure());
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

function* requestSitebuilderTemplateCategories() {
  const { token } = yield select(state => state.auth);
  const { jwt: jwtToken } = yield select(state => state.auth);
  const brand = isBRServer ? 'br' : 'es';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    url: 'api/v3/builder/categories',
  };

  try {
    yield put(onboardingActions.sitebuilder.templates.categories.setLoading(true));

    const response = yield call(api.whmcs.get, request);

    yield put(onboardingActions.sitebuilder.templates.categories.set(response.data.categories));
    yield put(onboardingActions.sitebuilder.templates.categories.success());

    yield put(onboardingActions.sitebuilder.templates.categories.setLoading(false));
  } catch (e) {
    yield put(onboardingActions.sitebuilder.templates.categories.setLoading(false));

    yield put(onboardingActions.sitebuilder.templates.categories.failure());
  }
}

function* requestSitebuilderTemplates({ payload }) {
  const { token } = yield select(state => state.auth);
  const { jwt: jwtToken } = yield select(state => state.auth);
  const { brand } = payload;

  const params = new URLSearchParams();

  if (payload.page) params.set('page', payload.page);
  if (payload.count) params.set('count', payload.count);
  if (payload.category) params.set('category', payload.category);

  const queryString = params.toString() ? `?${params.toString()}` : '';

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
      'X-Brand': brand,
    },
    url: `api/v3/builder/templates${queryString}`,
  };

  try {
    yield put(onboardingActions.sitebuilder.templates.setLoading(true));

    const response = yield call(api.whmcs.get, request);

    const { templates } = response.data;
    const { pagination } = response.data;

    yield put(onboardingActions.sitebuilder.templates.set({ templates, pagination }));
    yield put(onboardingActions.sitebuilder.templates.setLoading(false));
    yield put(onboardingActions.sitebuilder.templates.success());
  } catch (e) {
    yield put(onboardingActions.sitebuilder.templates.setLoading(false));

    yield put(onboardingActions.sitebuilder.templates.failure());
  }
}

function* requestSitebuilderProvisioning({ payload }) {
  const { token } = yield select(state => state.auth);
  const { jwt: jwtToken } = yield select(state => state.auth);
  const { hostingId, callback } = payload;

  const request = {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Client-ID': jwtToken,
    },
    url: 'api/v2/clientarea/br/newbuilder',
    data: {
      hostingId,
    },
  };

  try {
    const response = yield call(api.whmcs.post, request);

    callback && callback({ builderAddonId: response.data.addonId });

    yield put(onboardingActions.sitebuilder.provisioning.set({ builderAddonId: response.data.addonId }));
  } catch (e) {
    yield put(onboardingActions.sitebuilder.provisioning.failure());

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('onboardingHandler.genericErrorTitle'),
          info: locale('onboardingHandler.genericErrorInfo'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

function* sagas() {
  yield takeLatest(OnboardingActionTypes.ONBOARDING_REGISTER_REQUEST, registerOnboarding);
  yield takeLatest(OnboardingActionTypes.ONBOARDING_UPDATE_REQUEST, updateOnboarding);
  yield takeLatest(OnboardingActionTypes.WIZARD_REGISTER_REQUEST, registerWizard);
  yield takeLatest(OnboardingActionTypes.WIZARD_UPDATE_REQUEST, updateWizard);
  yield takeLatest(OnboardingActionTypes.BUSINESS_LIST_BUILDER_REQUEST, requestBusinessListBuilder);
  yield takeLatest(OnboardingActionTypes.REQUEST_SITEBUILDER_TEMPLATES, requestSitebuilderTemplates);
  yield takeLatest(OnboardingActionTypes.REQUEST_SITEBUILDER_CATEGORIES, requestSitebuilderTemplateCategories);
  yield takeLatest(OnboardingActionTypes.REQUEST_SITEBUILDER_PROVISIONING, requestSitebuilderProvisioning);
}

export default sagas;
